import axios from 'axios';
import { removeAuth, getAuth, setAuth } from '../modules/auth';

export const API_URL = process.env.REACT_APP_API_URL;

// Axios client for general requests
export const axiosClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Axios client for authenticated requests
export const axiosClientAuth = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if a refresh is in progress
let isRefreshing = false;
// Queue for requests waiting for the token refresh
let refreshSubscribers: { resolve: Function; reject: Function }[] = []; // Explicitly typed

// Request Interceptor: Attach access token to headers
axiosClientAuth.interceptors.request.use(
  (config) => {
    const auth = getAuth();
    if (auth && auth.access_token) {
      config.headers.Authorization = `Bearer ${auth.access_token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Function to refresh the access token using the refresh token
const refreshAccessToken = async (): Promise<string> => {
  const auth = getAuth();

  // If getAuth() returns undefined, throw an error
  if (!auth || !auth.refresh) {
    throw new Error('No refresh token available');
  }

  try {
    // Send refresh token to the backend to get a new access token
    const response = await axiosClient.post('/auth/refresh/', {
      refresh: auth.refresh,
    });
    const access = response?.data?.data?.access;
    const refresh = response?.data?.data?.refresh || auth.refresh;
    if (!access) {
      throw new Error('No access token returned from refresh endpoint');
    }
    // Store the new tokens in localStorage
    // setAuth({ access_token: access, refresh });
    const existingAuth = getAuth();
    setAuth({ ...existingAuth, access_token: access, refresh });

    return access;
  } catch (error) {
    console.error('Token refresh failed:', error);
    throw error;
  }
};

// Add subscribers to the queue (waiting requests)
const addSubscriber = (resolve: Function, reject: Function) => {
  refreshSubscribers.push({ resolve, reject });
};

// Notify all subscribers once the token refresh is complete
const onTokenRefreshed = (newAccessToken: string) => {
  refreshSubscribers.forEach((sub) => sub.resolve(newAccessToken));
  refreshSubscribers = []; // Clear the queue
};

// Notify all subscribers that the token refresh failed
const onRefreshFailed = (error: any) => {
  refreshSubscribers.forEach((sub) => sub.reject(error));
  refreshSubscribers = [];
};

// Response Interceptor: Handle expired token and retry the request
axiosClientAuth.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (originalRequest.url === '/auth/logout/') {
      return Promise.reject(error)
    }

    // If the token has expired (usually a 401 or 403 status code)
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true; // Flag to prevent infinite retries

      if (isRefreshing) {
        // If token refresh is in progress, queue the request
        return new Promise((resolve, reject) => {
          addSubscriber(resolve, reject);
        })
          .then(() => {
            const auth = getAuth();
            if (auth && auth.access_token) {
              originalRequest.headers['Authorization'] = `Bearer ${auth.access_token}`;
            }
            return axiosClientAuth(originalRequest); // Retry the original request
          })
          .catch((err) => Promise.reject(err));
      }

      // Set the flag to true indicating refresh is in progress
      isRefreshing = true;

      try {
        // Try to refresh the access token
        const newAccessToken = await refreshAccessToken();

        // Notify all queued requests with the new token
        onTokenRefreshed(newAccessToken);

        // Retry the original request with the new access token
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return axiosClientAuth(originalRequest);
      } catch (refreshError) {
        // If refreshing the token fails, remove the tokens and redirect to login
        onRefreshFailed(refreshError);
        removeAuth();
        // window.location.href = '/login';
        // fix added
        window.location.href = '/auth';
      } finally {
        // Reset the flag after the refresh process is done
        isRefreshing = false;
      }
    }

    // Return the error if it's not a token-related issue
    return Promise.reject(error);
  }
);
